import { OnInit, Component, DestroyRef, inject } from '@angular/core';
import { TillagePlanService } from '../proxy/operational/controllers';
import { ListService } from '@abp/ng.core';
import {
  CardComponent,
  CardBodyComponent,
  ConfirmationService,
} from '@abp/ng.theme.shared';
import {
  TillagePlanCreateDto,
  TillagePlanUpdateDto,
} from '../proxy/operational/dtos/tillage-plan';
import { TILLAGEPLAN_CONFIG, TILLAGEPLAN_FIELDS } from './tillage-plan.config';
import {
  BaseCrudComponent,
  createImportExcelModalConfig,
  CrudPage,
  DEFAULT_TOASTER_LIFE,
  ExcelImportErrorsExportLike,
  ExcelImportResultLike,
  GenericForm,
  IActionsSchema,
  IMPORT_AGAIN_LABEL,
  IMPORT_MAX_SIZE_MB,
  IMPORT_MODAL_TITLE,
  ModalBuilder,
  resolveActions,
  ZnExcelImportErrorsModal,
  ZnFileUploader,
} from '@zendra/shared';
import {
  TillagePlanPlanningOrchestratorService,
  TillagePlanProjectionPlanningOrchestratorService,
} from 'orchestrator';
import {
  GetTillagePlanProjectionListInputDto,
  TillagePlanProjectionDto,
  TillagePlanProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/tillage-plans/models';
import { Observable, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const IMPORT_TEMPLATE_FILE_NAME = 'TillagePlanImportTemplate.xlsx';

type TillagePlanImportResult = ExcelImportResultLike<unknown> & {
  fileName?: string;
};

@Component({
  selector: 'app-tillage-plan',
  imports: [CardComponent, CardBodyComponent, CrudPage, GenericForm],
  providers: [ListService, TillagePlanService, ConfirmationService],
  templateUrl: './tillage-plan.html',
  styleUrl: './tillage-plan.scss',
})
export class TillagePlan extends BaseCrudComponent<
  TillagePlanProjectionDto,
  TillagePlanCreateDto,
  TillagePlanUpdateDto,
  TillagePlanProjectionExcelDownloadDto
> implements OnInit {
  private readonly operationalService = inject(TillagePlanService);
  private readonly orchestratorService = inject(TillagePlanPlanningOrchestratorService);
  private readonly projectionOrchestratorService = inject(
    TillagePlanProjectionPlanningOrchestratorService,
  );
  service = {
    create: input => this.operationalService.create(input),
    update: (id, input) => this.operationalService.update(id, input),
    deleteByIds: ids => this.operationalService.deleteByIds(ids),
    getList: (query: GetTillagePlanProjectionListInputDto) =>
      this.projectionOrchestratorService.getList(query),
    getDownloadToken: () => this.projectionOrchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.projectionOrchestratorService.getListAsExcelFile(input),
    projectionConsistency: this.createProjectionConsistency(
      this.projectionOrchestratorService,
      'tillagePlanId',
    ),
  };
  config = TILLAGEPLAN_CONFIG;
  fields = TILLAGEPLAN_FIELDS;
  actions: IActionsSchema | null = null;

  private readonly modalService = inject(ModalBuilder);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    if (!this.config.actions || !this.config.actionIds) {
      return;
    }

    this.actions = this.withGridRefreshOnActionCompleted(
      resolveActions(this.config.actions, {
        [this.config.actionIds.calculatePercentageTillage]: () => this.calculatePercentageTillage(),
        [this.config.actionIds.importExcelModal]: () => this.openImportModal(),
        [this.config.actionIds.downloadExcelTemplateTrigger]: () => this.downloadExcelTemplate(),
      }),
    );
  }

  protected prepareUpdateDto(data: any, selected: TillagePlanProjectionDto): TillagePlanUpdateDto {
    return {
      weekNumber: data.weekNumber,
      year: data.year,
      tillageProgressArea: data.tillageProgressArea,
      tillageProgressPercentage: data.tillageProgressPercentage,
      periodId: data.periodId,
      propertyLocationId: data.propertyLocationId,
      vendorId: data.vendorId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'TillagePlan';
  }

  private calculatePercentageTillage(): Observable<unknown> | void {
    const ids = this.selectedRows;

    if (!ids?.length) {
      return;
    }

    return this.orchestratorService.calculateTillageProgressPercentage({ ids }).pipe(
      tap({
        next: () => {
          this.toaster.success('LanguageService::Actions:SuccessMessage', '', {
            life: DEFAULT_TOASTER_LIFE,
            messageLocalizationParams: [
              this.localizationService.instant(
                'Operational::Action.Trigger.CalculatePercentageTillage',
              ),
            ],
          });
        },
        error: err => {
          console.error(err);
        },
      }),
    );
  }

  private openImportModal(): void {
    const modalRef = this.modalService.openModal(
      ZnFileUploader,
      createImportExcelModalConfig({
        component: ZnFileUploader,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.TillagePlan',
        inputs: {
          maxSizeMb: IMPORT_MAX_SIZE_MB,
          importFile: (formData: FormData) =>
            this.orchestratorService.importTillagePlan(formData),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result?: TillagePlanImportResult) => {
        if (result?.hasErrors) {
          this.openImportErrorsModal(result);
          return;
        }

        if (result) {
          this.refreshGrid();
        }
      });
  }

  private openImportErrorsModal(result: TillagePlanImportResult): void {
    const modalRef = this.modalService.openModal(
      ZnExcelImportErrorsModal,
      createImportExcelModalConfig({
        component: ZnExcelImportErrorsModal,
        title: IMPORT_MODAL_TITLE,
        subTitle: 'Operational::Entity.TillagePlan',
        confirmLabel: IMPORT_AGAIN_LABEL,
        dismissLabel: 'LanguageService::Close',
        size: 'lg',
        inputs: {
          fileName: result.fileName ?? '',
          errors: result.errors,
          title: IMPORT_MODAL_TITLE,
          subTitle: 'Operational::Entity.TillagePlan',
          exportErrors: (input: ExcelImportErrorsExportLike) =>
            this.orchestratorService.exportTillagePlanImportErrors(input),
        },
      }),
    );

    modalRef.closed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((modalResult?: { retry?: boolean }) => {
        if (modalResult?.retry) {
          this.openImportModal();
        }
      });
  }

  private downloadExcelTemplate(): void {
    this.orchestratorService.getTillagePlanImportTemplate().subscribe({
      next: result => {
        this.toaster.success('LanguageService::Actions:DownloadTemplateMessage', '', {
          life: DEFAULT_TOASTER_LIFE,
          messageLocalizationParams: [
            this.localizationService.instant(
              'LanguageService::Actions:ExcelTemplateDownload',
            ),
          ],
        });
        this.downloadFile(result, IMPORT_TEMPLATE_FILE_NAME);
      },
      error: error => {
        this.toaster.error(error, '', {
          life: DEFAULT_TOASTER_LIFE,
        });
      },
    });
  }
}
